// src/layouts/LayoutDesktop.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar/Sidebar";
import DesktopHeader from "../components/DesktopHeader/DesktopHeader";
import MobileHeader from "../components/MobileHeader/MobileHeader";
import BottomBar from "../components/BottomBar/BottomBar";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { useEmpresa } from "../contexts/EmpresaContext";
import { NIVEIS } from "../config/niveis";
import { buildDefaultSubtitle, normalizeTitleFromPath } from "../utils/pageHeaderMeta";

function useIsMobile(breakpoint = 900) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= breakpoint;
  });

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= breakpoint);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);

  return isMobile;
}

function LayoutDesktop({
  tema: temaProp,
  sidebarAberta: sidebarAbertaProp = true,
  titulo,
  subtitulo,
  children
}) {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const { empresaAtual } = useEmpresa();
  const { tema: temaContext } = useTheme();
  const tema = temaProp || temaContext;
  const location = useLocation();
  const locationKey = `${location.pathname || ""}${location.search || ""}`;
  const tituloFinal = titulo || normalizeTitleFromPath(location.pathname, "");
  const subtituloFinal = subtitulo ? subtitulo : buildDefaultSubtitle(location.pathname, tituloFinal);
  const isMobile = useIsMobile(900);
  const isKiosk = Number(usuario?.nivel ?? -1) === NIVEIS.KIOSK;
  const logoUrl = empresaAtual?.logoUrl || null;
  
  const [sidebarAberta, setSidebarAberta] = useState(sidebarAbertaProp);

  useEffect(() => {
    setSidebarAberta(sidebarAbertaProp);
  }, [sidebarAbertaProp]);

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 1100) {
        setSidebarAberta(false);
      } else {
        setSidebarAberta(true);
      }
    }

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Segurança: se alguma tela deixou o app com pointer-events/overflow travados
  // (ex.: overlay, modal, etc.), resetamos ao mudar a rota.
  useEffect(() => {
    try {
      document.body?.style?.removeProperty("pointer-events");
      document.documentElement?.style?.removeProperty("pointer-events");
      document.body?.style?.removeProperty("overflow");
    } catch {
      // noop
    }
  }, [locationKey]);

  const toggleSidebar = () => {
    setSidebarAberta((prev) => !prev);
  };

  if (isKiosk) {
    return (
      <div className={`app-root ${tema}`}>
        <div className="app-shell-mobile">
          <MobileHeader onToggleSidebar={() => {}} titulo={tituloFinal} isKiosk={true} logoUrl={logoUrl} />
          <main className="m-main">{children}</main>
          <BottomBar usuario={usuario} onNavigate={(rota) => navigate(rota)} />
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className={`app-root ${tema}`}>
        <Sidebar isOpen={sidebarAberta} onClose={toggleSidebar} />

        <div className="app-shell-mobile">
          <MobileHeader
            onToggleSidebar={toggleSidebar}
            titulo={tituloFinal}
          />

          <main className="m-main" key={locationKey}>
            {children}
          </main>

          <BottomBar usuario={usuario} onNavigate={(rota) => navigate(rota)} />
        </div>
      </div>
    );
  }

  return (
    <div className={`pc-root ${tema} ${sidebarAberta ? "pc-root-sidebar-open" : "pc-root-sidebar-closed"}`}>
      <Sidebar isOpen={sidebarAberta} />

      <div className="pc-main">
        <DesktopHeader
          titulo={tituloFinal}
          subtitulo={subtituloFinal}
          onToggleSidebar={toggleSidebar}
        />

        <main className="pc-content" key={locationKey}>
          {children}
        </main>
      </div>
    </div>
  );
}

export default LayoutDesktop;
